/** Turning a drawn Plotly figure into the two things a reader asks for next:. */

export interface ExportTrace {
  type?: string;
  name?: string;
  x?: unknown[];
  y?: unknown[];
  orientation?: string;
  showlegend?: boolean;
}

export interface ExportLayout {
  xaxis?: { title?: { text?: string } };
  yaxis?: { title?: { text?: string } };
  barmode?: string;
}

export interface ExportTable {
  columns: string[];
  rows: (string | number)[][];
}

/** An axis title, or a fallback, as a column heading. */
function axisName(axis: { title?: { text?: string } }, fallback: string): string {
  const text = axis?.title?.text?.trim();
  return text || fallback;
}

/** The value axis is x on a horizontal chart and y on a vertical one, and the category axis is the other. */
function horizontal(traces: ExportTrace[]): boolean {
  return traces.some(t => t.orientation === 'h');
}

/** One row per drawn point. */
export function table(traces: ExportTrace[], layout: ExportLayout): ExportTable {
  const drawn = (traces ?? []).filter(t => t && (t.x?.length || t.y?.length));
  const flip = horizontal(drawn);

  const category = axisName(flip ? layout?.yaxis : layout?.xaxis, 'category');
  const value = axisName(flip ? layout?.xaxis : layout?.yaxis, 'value');
  const named = drawn.some(t => t.name);

  const columns = named ? [category, 'series', value] : [category, value];
  const rows: (string | number)[][] = [];

  for (const trace of drawn) {
    const cats = (flip ? trace.y : trace.x) ?? [];
    const vals = (flip ? trace.x : trace.y) ?? [];
    const n = Math.max(cats.length, vals.length);

    for (let i = 0; i < n; i++) {
      const c = cats.length === 1 ? cats[0] : cats[i];
      const v = vals.length === 1 ? vals[0] : vals[i];
      if (c === undefined || v === undefined || v === null) {
        continue;         // the invisible anchor trace, and gaps in a line
      }
      rows.push(named ? [String(c), trace.name ?? '', v as number]
                      : [String(c), v as number]);
    }
  }

  return { columns, rows };
}

export function tsv(data: ExportTable): string {
  const clean = (cell: string | number) =>
    typeof cell === 'string' ? cell.replace(/[\t\r\n]/g, ' ') : String(cell);
  return [data.columns.join('\t'), ...data.rows.map(r => r.map(clean).join('\t'))].join('\n') + '\n';
}

function quote(text: string): string {
  return "'" + String(text).replace(/\\/g, '\\\\').replace(/'/g, "\\'") + "'";
}

/** A runnable script that redraws the figure from the same numbers. */
export function python(data: ExportTable, opts: {
  title: string; source: string; kind: string; horizontal: boolean;
}): string {
  const [category, ...rest] = data.columns;
  const series = data.columns.length === 3 ? data.columns[1] : null;
  const value = data.columns[data.columns.length - 1];

  const body = data.rows
    .map(row => '    [' + row.map(c => typeof c === 'number' ? c : quote(c)).join(', ') + '],')
    .join('\n');

  const plot = opts.kind === 'box'
    ? boxPlot(category, value, opts.horizontal)
    : barPlot(category, series, value, opts.horizontal);

  return `# ${opts.title}
#
# Generated by VDJbase. The rows below are the figure as it was drawn - the same
# filters, the same order - so this reproduces it exactly rather than re-querying.
# To pull the data again instead:
#   ${opts.source}
#
# pip install pandas matplotlib

import pandas as pd
import matplotlib.pyplot as plt

COLUMNS = [${data.columns.map(quote).join(', ')}]

ROWS = [
${body}
]

df = pd.DataFrame(ROWS, columns=COLUMNS)

${plot}
ax.set_title(${quote(opts.title)})
fig.tight_layout()
fig.savefig('figure.png', dpi=300)
plt.show()
`;
}

function barPlot(category: string, series: string | null, value: string,
                 flip: boolean): string {
  const kind = flip ? 'barh' : 'bar';
  if (!series) {
    return `fig, ax = plt.subplots(figsize=(8, max(3, 0.28 * len(df))))
df.plot.${kind}(x=${quote(category)}, y=${quote(value)}, ax=ax, legend=False)
ax.set_xlabel(${quote(flip ? value : category)})
ax.set_ylabel(${quote(flip ? category : value)})`;
  }
  return `wide = df.pivot_table(index=${quote(category)}, columns=${quote(series)},
                      values=${quote(value)}, sort=False)

fig, ax = plt.subplots(figsize=(8, max(3, 0.28 * len(wide))))
wide.plot.${kind}(ax=ax)
ax.set_xlabel(${quote(flip ? value : category)})
ax.set_ylabel(${quote(flip ? category : value)})
ax.legend(title=None, frameon=False)`;
}

function boxPlot(category: string, value: string, flip: boolean): string {
  return `groups = list(dict.fromkeys(df[${quote(category)}]))      # drawn order, not sorted
data = [df.loc[df[${quote(category)}] == g, ${quote(value)}].to_numpy() for g in groups]

fig, ax = plt.subplots(figsize=(8, max(3, 0.4 * len(groups))))
ax.boxplot(data, labels=groups, vert=${flip ? 'False' : 'True'})
ax.set_xlabel(${quote(flip ? value : category)})
ax.set_ylabel(${quote(flip ? category : value)})`;
}


// ---------------------------------------------------------------- downloads

/** Allele names carry * and other characters a filename should not. */
export function safeStem(name: string): string {
  return (name || 'figure').replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-|-$/g, '');
}

export function saveText(text: string, filename: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: `${type};charset=utf-8` }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** What a panel has to say about its figure for the two downloads to work. */
export interface ExportSpec {
  name: string;
  title: string;
  source?: string;
  data?: ExportTrace[];
  layout?: ExportLayout;
  /** For a figure whose traces are not its data. */
  table?: ExportTable;
  script?: string;
}

/** Icons drawn here rather than taken from Plotly.Icons: the config is built before Plotly has necessarily loaded, and these are two paths. */
const DOWNLOAD_ICON = {
  width: 1000, height: 1000,
  path: 'M440 120 h120 v330 h170 L500 700 L270 450 h170 Z M180 760 h640 v120 H180 Z',
};

const CODE_ICON = {
  width: 1000, height: 1000,
  path: 'M336 128 L128 500 L336 872 L420 800 L272 500 L420 200 Z '
      + 'M664 128 L580 200 L728 500 L580 800 L664 872 L872 500 Z',
};

/** The two downloads as Plotly modebar buttons. */
export function exportButtons(spec: () => ExportSpec): unknown[] {
  /** Refuses to hand over an empty table. */
  const rows = (it: ExportSpec): ExportTable => {
    const data = it.table ?? table(it.data ?? [], it.layout ?? {});
    if (!data.rows.length) {
      throw new Error(
        `Nothing to export for "${it.name}": its traces carry no plottable x/y pairs. `
        + 'A figure whose traces are not its data must pass `table` (and usually `script`) '
        + 'to exportButtons.');
    }
    return data;
  };

  return [
    {
      name: 'downloadData',
      title: 'Download the plotted data (TSV)',
      icon: DOWNLOAD_ICON,
      click: () => {
        const it = spec();
        saveText(tsv(rows(it)), `${safeStem(it.name)}.tsv`, 'text/tab-separated-values');
      },
    },
    {
      name: 'downloadCode',
      title: 'Download a Python script that redraws this figure',
      icon: CODE_ICON,
      click: () => {
        const it = spec();
        const traces = it.data ?? [];
        const script = it.script ?? python(rows(it), {
          title: it.title || it.name,
          source: it.source ?? '',
          kind: traces.find(t => t?.type)?.type ?? 'bar',
          horizontal: traces.some(t => t?.orientation === 'h'),
        });
        saveText(script, `${safeStem(it.name)}.py`, 'text/x-python');
      },
    },
  ];
}
